import React, { useEffect, useMemo } from 'react';
import classNames from 'classnames';
import moment from 'moment';
import { PANELS, PANELS_TYPES } from 'app/containers/MainDashboard/consts.js';
import useCueData from 'app/containers/MainDashboard/hooks/react-query/useCueData.js';
import Spinner, { SPINNER_SIZES } from 'shared/components/andtComponents/Spinner.jsx';
import { useRootStore } from 'app/contexts/RootStoreContext.jsx';
import { Routes } from 'shared/constants/routes.js';
import { getFirstDayOfMonth, getSearchParamsPanel, sumTotalCost } from 'app/containers/MainDashboard/utils.js';
import { useMainDashboardContext } from 'app/containers/MainDashboard/contexts/mainDashboardContext.jsx';
import UsageCost from '../components/UsageCost.jsx';
import styles from './costPanels.module.scss';

const MtdCost = ({ currentOverrideCurrency, withOverrideApiKey }) => {
  const { updatePanelLoadingState, dateFromLastMonth, getDynamicFilters, getCurrentDay } = useMainDashboardContext();
  const { appStore } = useRootStore();
  const cueHook = useCueData(!appStore.isChangeAccountLoading);

  const mtdCostParams = {
    ...PANELS[PANELS_TYPES.MTD_COST].params,
    start: getFirstDayOfMonth(moment(getCurrentDay(withOverrideApiKey?.accountKey))),
    end: moment(getCurrentDay(withOverrideApiKey?.accountKey)).format('YYYY-MM-DD'),
    ...getDynamicFilters(),
    isPpApplied: appStore.isPpApplied,
    withOverrideApiKey,
  };
  const { data: mtdData, isLoading: isMtdLoading } = cueHook.fetchCueData(mtdCostParams);
  const { data: previousMtdData, isLoading: isPreviousMtdLoading } = cueHook.fetchCueData({
    ...PANELS[PANELS_TYPES.MTD_COST].params,
    start: getFirstDayOfMonth(dateFromLastMonth(withOverrideApiKey?.accountKey)),
    end: dateFromLastMonth(withOverrideApiKey?.accountKey).format('YYYY-MM-DD'),
    ...getDynamicFilters(),
    isPpApplied: appStore.isPpApplied,
    withOverrideApiKey,
  });

  const isLoading = isMtdLoading || isPreviousMtdLoading;
  const mtdCost = useMemo(() => sumTotalCost(mtdData), [mtdData]);

  const previousMtdCost = useMemo(() => sumTotalCost(previousMtdData), [previousMtdData]);

  const lastMonthPercent = useMemo(
    () => (previousMtdCost ? ((previousMtdCost - mtdCost) * 100) / previousMtdCost : null),
    [previousMtdCost, mtdCost],
  );

  useEffect(() => {
    updatePanelLoadingState(PANELS_TYPES.MTD_COST, isLoading);
  }, [isLoading, updatePanelLoadingState]);

  return (
    <div className={classNames(styles.costPanel, styles.multipleCostPanel)}>
      {isLoading ? (
        <Spinner className={styles.spinner} size={SPINNER_SIZES.MEDIUM} />
      ) : (
        <UsageCost
          overrideCurrency={currentOverrideCurrency}
          title={PANELS[PANELS_TYPES.MTD_COST].title}
          value={mtdCost}
          moreDetails={[{ title: PANELS[PANELS_TYPES.MTD_COST].secondTitle, value: previousMtdCost, isCurrency: true }]}
          percent={!isNaN(lastMonthPercent) ? `${lastMonthPercent * -1}` : null}
          showIcon
          infoValue={PANELS[PANELS_TYPES.MTD_COST].info}
          navigateTo={{
            path: Routes.COST_USAGE_EXPLORER,
            searchParams: getSearchParamsPanel(mtdCostParams, appStore.isPpApplied),
          }}
          showPercent={lastMonthPercent}
          tooltip="Open Cost & Usage Explorer page"
        />
      )}
    </div>
  );
};

export default MtdCost;
